import React, { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useApp } from '../context/AppContext';
import { MockQRCode } from '../components/MockQRCode';
import { ArrowLeft, Copy, Calendar, Gift, Check } from 'lucide-react';
interface RewardDetailPageProps {
  claimedRewardId: string;
  onNavigate: (page: string) => void;
}
export function RewardDetailPage({
  claimedRewardId,
  onNavigate
}: RewardDetailPageProps) {
  const {
    claimedRewards
  } = useApp();
  const [copiedCoupon, setCopiedCoupon] = useState(false);
  const reward = claimedRewards.find(r => r.id === claimedRewardId);
  // Fallback if reward not found
  if (!reward) {
    return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-1249">
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-1250">
          <div className="text-center" data-id="element-1251">
            <div className="text-5xl mb-4" data-id="element-1252">🎁</div>
            <h2 className="text-xl font-normal text-gray-900 mb-2" data-id="element-1253">
              Premio non trovato
            </h2>
            <p className="text-sm text-gray-500 mb-6" data-id="element-1254">
              Questo premio non esiste o è stato rimosso
            </p>
            <button onClick={() => onNavigate('market')} className="px-6 py-2.5 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-colors" data-id="element-1255">
              Torna ai premi
            </button>
          </div>
        </div>
      </div>;
  }
  const handleCopyCoupon = () => {
    navigator.clipboard.writeText(reward.couponCode);
    setCopiedCoupon(true);
    setTimeout(() => setCopiedCoupon(false), 2000);
  };
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-1256">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 px-6 py-4 flex items-center z-30 bg-white border-b border-gray-100" data-id="element-1257">
        <button onClick={() => onNavigate('market')} className="flex items-center gap-1.5 text-gray-900 hover:text-green-600 transition-colors" data-id="element-1258">
          <ArrowLeft className="h-6 w-6" strokeWidth={1.5} data-id="element-1259" />
          <span className="text-sm font-normal" data-id="element-1260">Indietro</span>
        </button>
        <h1 className="flex-1 text-center text-lg font-normal text-gray-900 pr-16" data-id="element-1261">
          Il tuo premio
        </h1>
      </header>

      {/* Main Content */}
      <ScrollArea className="flex-1 pt-16" data-id="element-1262">
        <div className="px-6 py-8 max-w-md mx-auto space-y-6" data-id="element-1263">
          {/* Reward Logo */}
          {reward.logo && <div className="flex justify-center" data-id="element-1264">
              <img src={reward.logo} alt={reward.name} className="h-16 w-auto object-contain" data-id="element-1265" />
            </div>}

          {/* Reward Name */}
          <div className="text-center" data-id="element-1266">
            <h2 className="text-2xl font-normal text-gray-900 mb-2" data-id="element-1267">
              {reward.name}
            </h2>
            {reward.description && <p className="text-sm text-gray-600" data-id="element-1268">{reward.description}</p>}
          </div>

          {/* QR Code */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100" data-id="element-1269">
            <div className="flex justify-center mb-4" data-id="element-1270">
              <MockQRCode value={reward.qrValue} size={200} data-id="element-1271" />
            </div>
            <p className="text-xs text-center text-gray-500" data-id="element-1272">
              Mostra questo codice QR al momento dell'acquisto
            </p>
          </div>

          {/* Coupon Code */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100" data-id="element-1273">
            <div className="flex items-center justify-between mb-3" data-id="element-1274">
              <span className="text-sm font-normal text-gray-600" data-id="element-1275">
                Codice coupon
              </span>
              <button onClick={handleCopyCoupon} className="flex items-center gap-1 text-xs font-normal text-green-600 hover:text-green-700 transition-colors" data-id="element-1276">
                {copiedCoupon ? <>
                    <Check className="h-3.5 w-3.5" data-id="element-1277" />
                    Copiato!
                  </> : <>
                    <Copy className="h-3.5 w-3.5" data-id="element-1278" />
                    Copia
                  </>}
              </button>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-1279">
              <p className="text-2xl font-mono font-normal text-center text-gray-900 tracking-wider" data-id="element-1280">
                {reward.couponCode}
              </p>
            </div>
          </div>

          {/* Reward Details */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 space-y-4" data-id="element-1281">
            <div data-id="element-1282">
              <p className="text-xs text-gray-500 mb-1" data-id="element-1283">Valore</p>
              <p className="text-base font-normal text-gray-900" data-id="element-1284">
                €{reward.value}
              </p>
            </div>

            <div className="h-px bg-gray-100" data-id="element-1285" />

            <div data-id="element-1286">
              <p className="text-xs text-gray-500 mb-1" data-id="element-1287">Scadenza</p>
              <p className="text-base font-normal text-gray-900" data-id="element-1288">
                {formatDate(reward.expiresAt)}
              </p>
            </div>

            <div className="h-px bg-gray-100" data-id="element-1289" />

            <div data-id="element-1290">
              <p className="text-xs text-gray-500 mb-1" data-id="element-1291">Ottenuto il</p>
              <p className="text-base font-normal text-gray-900" data-id="element-1292">
                {formatDate(reward.claimedAt)}
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-1293">
            <p className="text-xs text-blue-900 leading-relaxed" data-id="element-1294">
              💡 <span className="font-medium" data-id="element-1295">Come usare:</span> Mostra il
              codice QR o inserisci il codice coupon al momento del pagamento
              per applicare lo sconto. Valido fino alla data di scadenza.
            </p>
          </div>

          {/* CTA Button */}
          <button onClick={() => onNavigate('market')} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-green-600 bg-white border-2 border-green-600 hover:bg-green-50 transition-all" data-id="element-1296">
            Torna ai premi
          </button>
        </div>
      </ScrollArea>
    </div>;
}